import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import { SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR } from 'api/actionTypes';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import {
  INVENTORY_ITEM_STATUSES_OPTIONS,
  INVENTORY_ITEM_TYPE_SUPRA,
} from 'common/inventoryConstants';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import ButtonDeprecated, { BUTTON_THEME_BLUE_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const FORM_STATE_INITIAL = 'initial';
const FORM_STATE_SERIAL_SET = 'serialNumberSet';

const getFormFields = ({ setIsBarcodeScannerOpen, setFocusedField, isSupraTakedown }) => {
  return [
    {
      id: 'supraLockSerialNumber',
      title: 'Supra Lock Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('supraLockSerialNumber');
        },
      },
    },
    showFieldIf(isSupraTakedown, {
      id: 'supraLockStatus',
      title: 'Supra Lock Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    }),
  ];
};

const SrSunSetupDriverJobSupraBarcode = ({
  itemScans,
  listingAssignedItems,
  isSupraTakedown,
  handleSupraRegistration,
  getCurrentLocation,
  requestGeolocationPermission,
}) => {
  const [viewState, setViewState] = useState(
    itemScans.length > 0 ? FORM_STATE_SERIAL_SET : FORM_STATE_INITIAL,
  );

  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const dispatch = useDispatch();

  const _onSupraLock = () => {
    setViewState(itemScans.length > 0 ? FORM_STATE_SERIAL_SET : FORM_STATE_INITIAL);
  };

  const _onSubmit = async (formValues) => {
    const location = await _requestGeolocationPermission();
    const { latitude, longitude } = location;

    const { supraLockSerialNumber, supraLockStatus } = formValues;

    handleSupraRegistration({
      type: INVENTORY_ITEM_TYPE_SUPRA,
      serialNumber: supraLockSerialNumber,
      latitude,
      longitude,
      ...(isSupraTakedown && { status: supraLockStatus }),
    }).then(() => {
      return _onSupraLock();
    });
  };

  const _createGeolocationError = ({ errorMessage }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
      },
    });
  };

  const _requestGeolocationPermission = async () => {
    setIsLocationLoading(true);
    try {
      await requestGeolocationPermission();
      const location = await getCurrentLocation();

      setIsLocationLoading(false);
      return location;
    } catch (error) {
      _createGeolocationError({
        errorMessage: error.message,
      });
      setIsLocationLoading(false);
      throw error;
    }
  };

  const _onFieldScan = ({ barcode, formikBag }) => {
    if (!focusedField) {
      return;
    }
    formikBag.setFieldValue(focusedField, barcode);
    setFocusedField(null);
    setIsBarcodeScannerOpen(false);
  };

  const _handleScannerClose = () => {
    setIsBarcodeScannerOpen(false);
    setFocusedField(null);
  };

  const getItemScansSerialNumberMapping = () => {
    const itemScansSerialNumberMapping = {};
    itemScans.forEach((itemScan) => {
      itemScansSerialNumberMapping[itemScan.inventoryItemTypeId] = {
        serialNumber: itemScan.serialNumber,
        status: itemScan.status,
      };
    });
    return itemScansSerialNumberMapping;
  };

  const itemScansSerialNumberMapping = getItemScansSerialNumberMapping();

  const initialValues = {
    supraLockSerialNumber:
      listingAssignedItems[INVENTORY_ITEM_TYPE_SUPRA]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_SUPRA]?.serialNumber ||
      null,
    supraLockStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_SUPRA]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_SUPRA]?.status ||
      null,
  };

  return (
    <div styleName="card" className="fieldsContainer" id="field-comboLockboxId">
      <span styleName="cardTitle">Register Supra Lock</span>

      <SrBox margin="16px 0 0">
        {viewState === FORM_STATE_SERIAL_SET && (
          <>
            <SrBox styleName="jobDetailsBox" padding="16px 0 16px">
              <SrBox marginBottom="16px">
                <SrBox styleName="title">Supra Lock Barcode Number:</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_SUPRA]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_SUPRA]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_SUPRA]?.serialNumber || '--'}
                </SrBox>
              </SrBox>
            </SrBox>
            <SrBox margin="16px 0 0">
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(FORM_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </>
        )}
        {viewState === FORM_STATE_INITIAL && (
          <Formik
            onSubmit={_onSubmit}
            initialValues={createInitialValues({
              formFields: getFormFields({
                setIsBarcodeScannerOpen,
                setFocusedField,
                isSupraTakedown,
              }),
              defaultValues: initialValues,
            })}
            validate={() =>
              createFormValidationFn(
                getFormFields({
                  setIsBarcodeScannerOpen,
                  setFocusedField,
                  isSupraTakedown,
                }),
              )
            }
            render={(formikBag) => {
              const formFields = getFormFields({
                setFocusedField,
                setIsBarcodeScannerOpen,
                isSupraTakedown,
              });
              return (
                <Form>
                  {formFields.map((formField) => (
                    <SrFormikField
                      formikBag={formikBag}
                      formField={formField}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                    />
                  ))}
                  {isBarcodeScannerOpen && (
                    <SrBarcodeScanner
                      onText={(barcode) => _onFieldScan({ barcode, formikBag })}
                      onClose={_handleScannerClose}
                    />
                  )}
                  <ButtonDeprecated
                    margin="24px 0 0"
                    type="submit"
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                    disabled={isLocationLoading}
                  >
                    {isLocationLoading ? 'Loading...' : 'Submit'}
                  </ButtonDeprecated>
                </Form>
              );
            }}
          />
        )}
      </SrBox>
    </div>
  );
};

SrSunSetupDriverJobSupraBarcode.propTypes = {
  itemScans: PropTypes.array.isRequired,
  isSupraTakedown: PropTypes.bool.isRequired,
  handleSupraRegistration: PropTypes.func.isRequired,
  getCurrentLocation: PropTypes.func.isRequired,
  requestGeolocationPermission: PropTypes.func.isRequired,
};

SrSunSetupDriverJobSupraBarcode.displayName = 'SrSunSetupDriverJobSupraBarcode';

export default SrSunSetupDriverJobSupraBarcode;
